import React, { useEffect, useState } from 'react'

import { useAppearance } from '@fourthwall/appearance'
import { Skeleton } from '@fourthwall/components'

import { BarChart } from '../../../chartsModule/components/BarChart/BarChart'
import { LineChart } from '../../../chartsModule/components/LineChart/LineChart'
import { chartColors } from '../../../chartsModule/consts'
import { AnalyticsChartLegend } from '../AnalyticsChartLegend/AnalyticsChartLegend'
import * as S from './AnalyticsCard.styled'
import type { AnalyticsCardProps, AnalyticsCardType } from './AnalyticsCard.types'
import { isBarChartType, isBarStatsType, isLineChartType, isStatsType } from './AnalyticsCard.types'
import AnalyticsCardHeading from './components/AnalyticsCardHeading/AnalyticsCardHeading'
import {
  BAR_DEFAULT_COLORS,
  BarStatsTypeAnalyticsCardContent,
} from './components/BarStatsTypeAnalyticsCardContent/BarStatsTypeAnalyticsCardContent'
import { StatsTypeAnalyticsCardContent } from './components/StatsTypeAnalyticsCardContent/StatsTypeAnalyticsCardContent'

export const AnalyticsCard = (props: AnalyticsCardProps) => {
  const { appearance: settingsAppearance } = useAppearance()
  const [appearance, setAppearance] = useState<'dark' | 'light'>('light')
  const isEmpty = props.data.length === 0 && !props.isDataLoading

  // TODO: Move appearance detection to the AppearanceProvider
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e: MediaQueryListEvent) => {
      setAppearance(e.matches ? 'dark' : 'light')
    }

    setAppearance(mediaQuery.matches ? 'dark' : 'light')

    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  useEffect(() => {
    if (settingsAppearance === 'light dark') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      setAppearance(mediaQuery.matches ? 'dark' : 'light')
    } else {
      setAppearance(settingsAppearance === 'dark' ? 'dark' : 'light')
    }
  }, [settingsAppearance])

  const getSpecificHeadingProps = () => {
    if (isLineChartType(props)) {
      return { value: isEmpty ? undefined : props.value }
    }

    if (isBarChartType(props)) {
      return { value: isEmpty ? undefined : props.value }
    }

    if (isStatsType(props)) {
      return {
        subtitle: props.legend,
        value: isEmpty ? undefined : props.value,
      }
    }

    return {}
  }

  const renderHeading = () => {
    const commonProps = {
      type: props.type,
      header: props.header,
      reportLink: props.reportLink,
      isDataLoading: props.isDataLoading,
      isDefinitionLoading: props.isDefinitionLoading,
      externalSource: props.externalSource,
      formatValue: props.formatValue,
    }

    return <AnalyticsCardHeading {...commonProps} {...getSpecificHeadingProps()} />
  }

  const renderContent = () => {
    if (isEmpty) {
      return (
        <>
          <S.EmptySpace />
          <S.EmptyMessage>{props.emptyMessage || 'No data'}</S.EmptyMessage>
        </>
      )
    }

    if (isLineChartType(props)) {
      return (
        <LineChart
          data={props.data}
          isLoading={props.isDataLoading}
          xAxisFormatter={props.formatXAxis}
          yAxisFormatter={props.formatYAxis || props.formatValue}
          appearance={appearance}
        />
      )
    }

    if (isBarChartType(props)) {
      return (
        <BarChart
          data={props.data}
          isLoading={props.isDataLoading}
          xAxisFormatter={props.formatXAxis}
          yAxisFormatter={props.formatYAxis || props.formatValue}
          appearance={appearance}
        />
      )
    }

    if (isBarStatsType(props)) {
      return (
        <BarStatsTypeAnalyticsCardContent
          data={props.data}
          isLoading={props.isDataLoading}
          formatValue={props.formatValue}
        />
      )
    }

    if (isStatsType(props)) {
      return (
        <StatsTypeAnalyticsCardContent
          data={props.data}
          isLoading={props.isDataLoading}
          formatValue={props.formatValue}
        />
      )
    }
  }

  const renderFooter = () => {
    if (isEmpty) {
      return null
    }

    if (isStatsType(props)) {
      return null
    }

    if (props.isDefinitionLoading) {
      return <Skeleton.Shape width={160} height={16} />
    }

    if (!props.legend) {
      return null
    }

    const legendColors: Omit<Record<AnalyticsCardType, string[]>, 'stats'> = {
      lineChart: [chartColors[appearance].series.primary, chartColors[appearance].series.secondary],
      barChart: [chartColors[appearance].series.primary, chartColors[appearance].series.secondary],
      barStats: [BAR_DEFAULT_COLORS[0], BAR_DEFAULT_COLORS[1]],
    }

    return (
      <AnalyticsChartLegend
        items={[
          {
            value: props.legend,
            color: legendColors[props.type][0],
          },
        ]}
      />
    )
  }

  return (
    <S.Container>
      {renderHeading()}
      <S.Content>{renderContent()}</S.Content>
      {renderFooter()}
    </S.Container>
  )
}
